<script setup lang="ts">
/**
 * Root Application Component
 *
 * Main app entry with global components
 */
import { onMounted, watch } from 'vue';
import { RouterView } from 'vue-router';
import { useTranslation } from 'i18next-vue';
import { ConfirmDialog } from '@/shared/components';
import { AppToast, provideTranslation, useIdleTimeout, useSessionSync } from '@tms-phoenix/ui-vue';
import { useUIStore, useAuthStore } from '@/stores';
import { useLocale } from '@/shared/composables/useLocale';
import { usePrimeVue } from 'primevue/config';

const uiStore = useUIStore();
const authStore = useAuthStore();
const { t } = useTranslation();
const { locale } = useLocale();
const { config: primeConfig } = usePrimeVue();

// ── PrimeVue calendar locale definitions ────────────────────────────────────

type PrimeVueLocaleConfig = {
  dayNames: string[];
  dayNamesShort: string[];
  dayNamesMin: string[];
  monthNames: string[];
  monthNamesShort: string[];
  today: string;
  clear: string;
  weekHeader: string;
  firstDayOfWeek: number;
};

const PRIMEVUE_LOCALES: Record<string, PrimeVueLocaleConfig> = {
  en: {
    dayNames: ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'],
    dayNamesShort: ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'],
    dayNamesMin: ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'],
    monthNames: ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'],
    monthNamesShort: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'],
    today: 'Today',
    clear: 'Clear',
    weekHeader: 'Wk',
    firstDayOfWeek: 0,
  },
  de: {
    dayNames: ['Sonntag', 'Montag', 'Dienstag', 'Mittwoch', 'Donnerstag', 'Freitag', 'Samstag'],
    dayNamesShort: ['So', 'Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa'],
    dayNamesMin: ['So', 'Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa'],
    monthNames: ['Januar', 'Februar', 'März', 'April', 'Mai', 'Juni', 'Juli', 'August', 'September', 'Oktober', 'November', 'Dezember'],
    monthNamesShort: ['Jan', 'Feb', 'Mär', 'Apr', 'Mai', 'Jun', 'Jul', 'Aug', 'Sep', 'Okt', 'Nov', 'Dez'],
    today: 'Heute',
    clear: 'Löschen',
    weekHeader: 'KW',
    firstDayOfWeek: 1,
  },
  fr: {
    dayNames: ['Dimanche', 'Lundi', 'Mardi', 'Mercredi', 'Jeudi', 'Vendredi', 'Samedi'],
    dayNamesShort: ['Dim', 'Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam'],
    dayNamesMin: ['D', 'L', 'M', 'M', 'J', 'V', 'S'],
    monthNames: ['Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin', 'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'],
    monthNamesShort: ['Jan', 'Fév', 'Mar', 'Avr', 'Mai', 'Juin', 'Juil', 'Aoû', 'Sep', 'Oct', 'Nov', 'Déc'],
    today: "Aujourd'hui",
    clear: 'Effacer',
    weekHeader: 'Sem',
    firstDayOfWeek: 1,
  },
  tr: {
    dayNames: ['Pazar', 'Pazartesi', 'Salı', 'Çarşamba', 'Perşembe', 'Cuma', 'Cumartesi'],
    dayNamesShort: ['Paz', 'Pzt', 'Sal', 'Çar', 'Per', 'Cum', 'Cmt'],
    dayNamesMin: ['Pz', 'Pt', 'Sa', 'Ça', 'Pe', 'Cu', 'Ct'],
    monthNames: ['Ocak', 'Şubat', 'Mart', 'Nisan', 'Mayıs', 'Haziran', 'Temmuz', 'Ağustos', 'Eylül', 'Ekim', 'Kasım', 'Aralık'],
    monthNamesShort: ['Oca', 'Şub', 'Mar', 'Nis', 'May', 'Haz', 'Tem', 'Ağu', 'Eyl', 'Eki', 'Kas', 'Ara'],
    today: 'Bugün',
    clear: 'Temizle',
    weekHeader: 'Hf',
    firstDayOfWeek: 1,
  },
};

/** Apply PrimeVue calendar locale; always resets to English when lang is null or unknown. */
function applyPrimeVueLocale(lang: string | null) {
  const localeData = PRIMEVUE_LOCALES[lang ?? 'en'] ?? PRIMEVUE_LOCALES.en;
  Object.assign(primeConfig.locale, localeData);
}

// Apply immediately if already available (e.g. page refresh after login)
applyPrimeVueLocale(authStore.defaultLanguage);

// Watch for language becoming available after login
watch(() => authStore.defaultLanguage, (lang) => {
  applyPrimeVueLocale(lang);
});

useIdleTimeout(
  async () => {
    await authStore.logout();
    // logout() already calls router.push('/login') internally
  },
  { active: () => authStore.isAuthenticated },
);

// Sign this tab out when another tab ends or replaces the stored session (e.g.
// a different person signs in after this one was signed out), so a stale tab
// cannot keep acting as — or write back the tokens of — the previous user.
useSessionSync({
  storageKey: 'pinia-auth',
  getUserId: () => authStore.user?.id ?? null,
});

// Provide the i18n translation function to all shared @tms-phoenix/ui-vue
// components so they auto-localize without explicit label props.
// Guard against i18next returning the raw key string for missing translations.
// When the key is missing, fall back to the explicit defaultValue (or the key).
provideTranslation(
  (key: string, defaultValue?: string) => {
    const result = t(key);
    return result === key ? (defaultValue ?? key) : result;
  },
  locale,
);

// Apply theme on mount
onMounted(() => {
  uiStore.applyTheme();
});
</script>

<template>
  <div
    id="app"
    class="h-screen overflow-hidden bg-app-gradient font-sans"
  >
    <!-- Router view with transitions -->
    <RouterView v-slot="{ Component, route }">
      <Transition
        :name="route.meta.transition as string || 'fade'"
        mode="out-in"
      >
        <component
          :is="Component"
          :key="route.path"
        />
      </Transition>
    </RouterView>

    <!-- Global Components -->
    <AppToast />
    <ConfirmDialog />
  </div>
</template>

<style>
/* Prevent page scroll */
html, body {
  overflow: hidden;
  height: 100vh;
  margin: 0;
  padding: 0;
}

/* App gradient background */
.bg-app-gradient {
  background: linear-gradient(to bottom right, var(--color-primary-100), var(--color-profile-50));
}

/* Fade transition */
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.15s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

/* Slide transition */
.slide-enter-active,
.slide-leave-active {
  transition: all 0.2s ease;
}

.slide-enter-from {
  opacity: 0;
  transform: translateX(-10px);
}

.slide-leave-to {
  opacity: 0;
  transform: translateX(10px);
}
</style>
